import { computed } from 'vue'
import { format, toZonedTime, fromZonedTime } from 'date-fns-tz'
import { isToday, isTomorrow, isYesterday } from 'date-fns'
import { de } from 'date-fns/locale'

// epg (air)time helper
// Composition API counterpart of the `epgAirTimeHelper` mixin (`../apps/tvmovie/theme/mixins/epg-air-time-helper.js`).
// That mixin is still used by several Options API components, so it's kept as-is - this composable exists for
// components that have been migrated to `<script setup>`.
// The `airTimeNow` computed property returns the current time rounded to the next 5 minutes in ISO format.
// The `getAirTimeByAirTimeOption` method converts a given airtime option into a JavaScript Date object.
// The `formatLocalDateTime` method formats a JavaScript Date object into a `datetime-local` compatible string.

const EPG_TIME_ZONE = 'Europe/Berlin' // our default timezone

export function useEpgAirTimeHelper () {
  const timeZone = EPG_TIME_ZONE

  // Returns the current time rounded to the next 5 minutes in ISO format
  const airTimeNow = computed(() => {
    // get current time from our timezone - important for correct display even if the user/google is in a different timezone
    const now = toZonedTime(new Date(), timeZone)
    now.setMinutes(Math.ceil(now.getMinutes() / 5) * 5, 0, 0) // round to next 5 minutes
    return now.toISOString()
  })

  const browserTimeZone = computed(() => Intl.DateTimeFormat().resolvedOptions().timeZone)

  /**
   * Converts a given airtime option into a JavaScript Date object.
   * @param airTimeOption - The airtime option to convert.
   * @param customFallbackAirTime - Value returned as-is for the 'custom' option (which needs no further conversion).
   * @returns The converted airtime in ISO format.
   */
  function getAirTimeByAirTimeOption (airTimeOption: string | null | undefined, customFallbackAirTime: string | null = null): string | null {
    if (!airTimeOption) return null

    // get current time from our timezone - important for correct display even if the user/google is in a different timezone
    const now = toZonedTime(new Date(), timeZone)
    now.setSeconds(0, 0) // set seconds & milliseconds to 0

    // Split option into date part and optional time part
    const [datePart, timePart] = airTimeOption.split(',')

    // Helper function to shift the current date to a specific day by adding days
    const shiftNowByDays = (days: number) => {
      now.setDate(now.getDate() + days)
    }

    // Helper function to apply a time string (HH:MM) to a date object
    const applyTimeToDate = (date: Date, time?: string) => {
      if (time) {
        const [hours, minutes] = time.split(':').map(Number)
        date.setHours(hours, minutes)
      }
    }

    const convertToUtcIsoString = (date: Date) => {
      return fromZonedTime(date, timeZone).toISOString()
    }

    switch (datePart) {
      case 'now':
        return airTimeNow.value

      case 'today':
        applyTimeToDate(now, timePart)
        return convertToUtcIsoString(now)

      case 'tomorrow':
        // Shift the current date to tomorrow
        shiftNowByDays(1)
        applyTimeToDate(now, timePart)
        return convertToUtcIsoString(now)

      case 'friday':
      case 'saturday':
      case 'sunday': {
        // Map day names to their corresponding day numbers (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
        // This allows us to calculate the next occurrence of the specified day
        const dayMap: Record<string, number> = { friday: 5, saturday: 6, sunday: 0 }
        // Get the target day number from the map with the provided datePart
        const targetDay = dayMap[datePart]
        // Calculate days to add to get to the target day
        // Add 7 to ensure we always get the next occurrence of the target day
        const daysToAdd = (targetDay - now.getDay() + 7) % 7
        // Set the date to the next occurrence of the target day by adding calculated daysToAdd
        shiftNowByDays(daysToAdd)
        applyTimeToDate(now, timePart)
        return convertToUtcIsoString(now)
      }

      default:
        return customFallbackAirTime // custom does not need any changes
    }
  }

  /**
   * Formats a JavaScript Date object into a `datetime-local` compatible string.
   *
   * @param date - The JavaScript Date object (or ISO string) to format.
   * @returns A string formatted as `YYYY-MM-DDTHH:MM`, compatible with HTML <input type="datetime-local">.
   *
   * Example:
   * ```js
   * const date = new Date('2025-04-02T14:30:00Z')
   * console.log(formatLocalDateTime(date)) // "2025-04-02T16:30" (if UTC+2)
   * ```
   */
  function formatLocalDateTime (date: Date | string): string {
    // ensure correct type
    const parsedDate = typeof date === 'string' ? new Date(date) : date
    // Ensures that numbers (month, day, hour, minute) are always two digits (e.g., "01" instead of "1").
    const pad = (num: number) => num.toString().padStart(2, '0')
    return `${parsedDate.getFullYear()}-${pad(parsedDate.getMonth() + 1)}-${pad(parsedDate.getDate())}T${pad(parsedDate.getHours())}:${pad(parsedDate.getMinutes())}`
  }

  /**
   * Converts a given date into the component's target time zone.
   * Step 1: Interpret the original date as if it were in the browser's time zone,
   *         then convert it into UTC.
   * Step 2: Re-interpret the UTC date into the target time zone and return it.
   */
  function forceDateIntoTimeZone (date: Date | string | number): Date {
    const utc = fromZonedTime(date, browserTimeZone.value) // 1. interpret original date as "local" in fromTimeZone
    return toZonedTime(utc, timeZone) // 2. reinterpret it in the target time zone and return it
  }

  /**
   * Formats a given airtime for display, e.g. "Heute, 02. Apr" or "Freitag, 04. Apr".
   */
  function displayAirDate (airTime: Date | string | number | null = null, dateFormat = 'default'): string | undefined {
    if (!airTime) return undefined

    const zoned = toZonedTime(airTime, timeZone)

    // generell formatString and prefix
    let formatString = dateFormat === 'default' ? 'EEEE, dd. MMM' : dateFormat
    let prefix = ''

    // for relative day (today/tomorrow) we modify the format string and prefix but not, if format !== 'default'
    if (dateFormat === 'default' && (isToday(zoned) || isTomorrow(zoned) || isYesterday(zoned))) {
      formatString = 'dd. MMM'
      prefix = `${isToday(zoned) ? 'Heute' : isTomorrow(zoned) ? 'Morgen' : 'Gestern'}, `
    }

    return prefix + format(zoned, formatString, { locale: de })
  }

  return {
    timeZone,
    airTimeNow,
    browserTimeZone,
    getAirTimeByAirTimeOption,
    formatLocalDateTime,
    forceDateIntoTimeZone,
    displayAirDate
  }
}
